import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Field from '@atlaskit/form/field';
import Form from '@atlaskit/form/form';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link/link';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Select from '@atlaskit/select/default';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';

import {
	type GenerateImageProps,
	IMAGE_GENERATION_MODELS,
	IMAGE_GENERATION_THEMES,
	type ImageGenerationModel,
	type ImageGenerationTheme,
} from '../../types';

const i18n = defineMessages({
	createImageLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.create-image-label',
		defaultMessage: 'Create an image with Rovo',
		description: 'Label text for the Rovo header generation entry point',
	},
	getCreativePlaceholder: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-placeholder',
		defaultMessage: 'Describe an image to create',
		description: 'Placeholder text for the Rovo header generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.rovo-header-image-generator.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description: 'Alternative text for the Rovo icon in the AI image generation interface.',
	},
	submitButtonLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.submit-button-label',
		defaultMessage: 'Generate AI header image',
		description: 'Label for the Rovo header generation submit button',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Placeholder text for the style/theme picker dropdown',
	},
	styleLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.style-label',
		defaultMessage: 'Style',
		description: 'Label for the style/theme picker dropdown',
	},
	stylePhotorealistic: {
		id: 'ai-image-generation.rovo-header-image-generator.style-photorealistic',
		defaultMessage: 'Photorealistic',
		description:
			'Style option in the image generation dropdown that creates realistic photograph-like images.',
	},
	stylePhotoAuto: {
		id: 'ai-image-generation.rovo-header-image-generator.style-photo-auto',
		defaultMessage: 'Photo (Auto)',
		description:
			'Style option in the image generation dropdown that uses the default photographic image style.',
	},
	styleAbstract: {
		id: 'ai-image-generation.rovo-header-image-generator.style-abstract',
		defaultMessage: 'Abstract',
		description:
			'Style option in the image generation dropdown that creates artistic images with geometric shapes and creative interpretations.',
	},
	styleWatercolors: {
		id: 'ai-image-generation.rovo-header-image-generator.style-watercolors',
		defaultMessage: 'Watercolors',
		description:
			'Style option in the image generation dropdown that creates watercolor painting-style images with soft, flowing colors.',
	},
	styleGraphicDesign: {
		id: 'ai-image-generation.rovo-header-image-generator.style-graphic-design',
		defaultMessage: 'Graphic Design',
		description:
			'Style option in the image generation dropdown that creates modern design images with clean layouts and bold typography.',
	},
	styleLandscape: {
		id: 'ai-image-generation.rovo-header-image-generator.style-landscape',
		defaultMessage: 'Landscape',
		description:
			'Style option in the image generation dropdown that creates landscape images featuring natural or urban scenery.',
	},
	styleSketch: {
		id: 'ai-image-generation.rovo-header-image-generator.style-sketch',
		defaultMessage: 'Sketch',
		description:
			'Style option in the image generation dropdown that creates sketch-style images with hand-drawn lines and shading.',
	},
	styleThreeD: {
		id: 'ai-image-generation.rovo-header-image-generator.style-3d',
		defaultMessage: '3D',
		description:
			'Style option in the image generation dropdown that creates three-dimensional rendered images.',
	},
	imageModelLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-label',
		defaultMessage: 'Image Model',
		description: 'Label for the image generation model picker dropdown.',
	},
	imageModelAuto: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-auto',
		defaultMessage: 'Auto',
		description: 'Image generation model option that lets the system choose the model.',
	},
	imageModelAutoDescription: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-auto-description',
		defaultMessage: 'balance of speed and quality',
		description: 'Description for the Auto image generation model option.',
	},
	imageModelFast: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-fast',
		defaultMessage: 'Fast',
		description: 'Image generation model option optimized for fast generation.',
	},
	imageModelFastDescription: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-fast-description',
		defaultMessage: 'quickest results',
		description: 'Description for the Fast image generation model option.',
	},
	imageModelHigh: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-high',
		defaultMessage: 'High',
		description: 'Image generation model option optimized for high quality generation.',
	},
	imageModelHighDescription: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-high-description',
		defaultMessage: 'pro-quality image generation',
		description: 'Description for the High image generation model option.',
	},
	imageModelMax: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-max',
		defaultMessage: 'Max',
		description: 'Image generation model option optimized for maximum quality generation.',
	},
	imageModelMaxDescription: {
		id: 'ai-image-generation.rovo-header-image-generator.image-model-max-description',
		defaultMessage: 'best detail at 2K resolution',
		description: 'Description for the Max image generation model option.',
	},
	footerMessage: {
		id: 'ai-image-generation.rovo-header-image-generator.footer-message',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Footer disclaimer message informing users that results are AI-generated',
	},
});

interface ImageGenerationFieldData {
	promptInput: string;
}

type ThemeOption = {
	label: string;
	value: ImageGenerationTheme | 'none';
};

type ImageGenerationModelOption = {
	description: string;
	label: string;
	value: ImageGenerationModel;
};

type RovoHeaderImageGeneratorProps = {
	/**
	 * Controls whether users can choose the image generation model.
	 */
	allowImageModelSelection?: boolean;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
	isAiHeaderImageLoading?: boolean;
	isDisabled?: boolean;
	/**
	 * Optional extra action to render inline to the left of the submit button.
	 * The caller owns the element + its `onClick`; this component only renders it.
	 */
	moreActionsButton?: React.ReactNode;
	/**
	 * Optional callback fired when the user submits the prompt form, i.e.
	 * after the (enabled) submit button is clicked and the form passes
	 * validation (`promptInput` is required). Receives the theme picked by
	 * the user (or `'none'` if no theme is selected) and image model picked
	 * by the user (or `'none'` if the model picker is not shown) so callers
	 * can include them in their own analytics. Fired synchronously before
	 * image generation begins.
	 */
	onSubmit?: (info: {
		imageGenerationModel: ImageGenerationModel | 'none';
		selectedTheme: ImageGenerationTheme | 'none';
	}) => void;
	/**
	 * Optional text to prefill (and overwrite) the prompt text area.
	 * When provided (non-empty), this replaces any existing user input in the box.
	 */
	prefillText?: string;
	/**
	 * Limits the style picker to the options supported by Remix image generation.
	 */
	useRemixStyleOptions?: boolean;
};

const styles = cssMap({
	divider: {
		width: '100%',
		height: '1px',
		marginBlock: token('space.075'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	rovoHeader: {
		marginBlockEnd: token('space.075'),
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.100'),
		marginBlockStart: token('space.150'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
	actionsRow: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	actionsLeft: {
		display: 'flex',
		alignItems: 'center',
	},
	footer: {
		color: token('color.text.subtlest'),
		paddingBlockStart: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
		marginBlockStart: token('space.negative.075'),
		marginBlockEnd: token('space.negative.150'),
	},
});

export const RovoHeaderImageGenerator = ({
	allowImageModelSelection = false,
	generateAIHeaderImage,
	isAiHeaderImageLoading,
	isDisabled,
	moreActionsButton,
	onSubmit,
	prefillText,
	useRemixStyleOptions = false,
}: RovoHeaderImageGeneratorProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedTheme, setSelectedTheme] = useState<ThemeOption | null>(null);
	const [selectedImageGenerationModel, setSelectedImageGenerationModel] =
		useState<ImageGenerationModel>(IMAGE_GENERATION_MODELS.auto);
	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation({
			product: 'confluence',
			subproduct: 'confluence',
			aiFeatureName: 'aiHeaderImageGeneration',
			proactiveGeneratedAI: 0,
			userGeneratedAI: 1,
			enablePlatformEvents: true,
			source: 'rovoHeaderImageGenerator',
		});

	const legacyThemeOptions: ThemeOption[] = [
		{
			label: formatMessage(i18n.stylePhotorealistic),
			value: IMAGE_GENERATION_THEMES.photorealistic,
		},
		{ label: formatMessage(i18n.styleThreeD), value: IMAGE_GENERATION_THEMES.threeD },
		{ label: formatMessage(i18n.styleSketch), value: IMAGE_GENERATION_THEMES.sketch },
		{ label: formatMessage(i18n.styleWatercolors), value: IMAGE_GENERATION_THEMES.watercolors },
		{ label: formatMessage(i18n.styleAbstract), value: IMAGE_GENERATION_THEMES.abstract },
		{
			label: formatMessage(i18n.styleGraphicDesign),
			value: IMAGE_GENERATION_THEMES.graphic_design,
		},
		{ label: formatMessage(i18n.styleLandscape), value: IMAGE_GENERATION_THEMES.landscape },
	];
	const remixThemeOptions: ThemeOption[] = [
		{ label: formatMessage(i18n.stylePhotoAuto), value: 'none' },
		{
			label: formatMessage(i18n.styleThreeD),
			value: IMAGE_GENERATION_THEMES.headerImagesThreeD,
		},
		{
			label: formatMessage(i18n.styleSketch),
			value: IMAGE_GENERATION_THEMES.headerImagesSketch,
		},
		{
			label: formatMessage(i18n.styleWatercolors),
			value: IMAGE_GENERATION_THEMES.headerImagesWatercolors,
		},
	];
	const themeOptions = useRemixStyleOptions ? remixThemeOptions : legacyThemeOptions;

	const imageGenerationModelOptions: ImageGenerationModelOption[] = [
		{
			description: formatMessage(i18n.imageModelMaxDescription),
			label: formatMessage(i18n.imageModelMax),
			value: IMAGE_GENERATION_MODELS.max,
		},
		{
			description: formatMessage(i18n.imageModelHighDescription),
			label: formatMessage(i18n.imageModelHigh),
			value: IMAGE_GENERATION_MODELS.high,
		},
		{
			description: formatMessage(i18n.imageModelAutoDescription),
			label: formatMessage(i18n.imageModelAuto),
			value: IMAGE_GENERATION_MODELS.auto,
		},
		{
			description: formatMessage(i18n.imageModelFastDescription),
			label: formatMessage(i18n.imageModelFast),
			value: IMAGE_GENERATION_MODELS.fast,
		},
	];
	const selectedImageGenerationModelOption =
		imageGenerationModelOptions.find((option) => option.value === selectedImageGenerationModel) ??
		imageGenerationModelOptions[0];

	const effectivePrefillText = prefillText?.trim() ? prefillText : undefined;

	// Atlaskit Form stores values by field name; `defaultValue` only applies on mount.
	// When `prefillText` changes, force overwrite the form state for `promptInput`.
	const setFieldValueRef = useRef<null | ((name: string, value: string) => void)>(null);
	const lastAppliedPrefillTextRef = useRef<string | undefined>(undefined);

	const isLoading = isAiHeaderImageLoading ?? false;
	const isDisabledState = isLoading || Boolean(isDisabled);
	const handleThemeChange = useCallback((newValue: ThemeOption | null) => {
		setSelectedTheme((currentTheme) => (currentTheme?.value === newValue?.value ? null : newValue));
	}, []);
	const imageGenerationModelPicker = allowImageModelSelection ? (
		<Box>
			<label htmlFor="image-generation-model-picker">
				<Text size="small" weight="semibold">
					{formatMessage(i18n.imageModelLabel)}
				</Text>
			</label>
			<Box paddingBlockStart="space.050">
				<Select<ImageGenerationModelOption>
					inputId="image-generation-model-picker"
					options={imageGenerationModelOptions}
					value={selectedImageGenerationModelOption}
					onChange={(newValue) =>
						setSelectedImageGenerationModel(newValue?.value ?? IMAGE_GENERATION_MODELS.auto)
					}
					formatOptionLabel={(option, { context }) =>
						context === 'menu' ? `${option.label} - ${option.description}` : option.label
					}
					isDisabled={isDisabledState}
				/>
			</Box>
		</Box>
	) : null;

	const handleFormSubmit = async (data: ImageGenerationFieldData) => {
		if (isDisabledState) {
			return;
		}

		const selectedThemeValue = selectedTheme?.value === 'none' ? undefined : selectedTheme?.value;
		const useRawPrompt = !selectedThemeValue;

		// Notify the caller that the (enabled, valid) submit happened so they
		// can fire their own analytics. The caller owns this callback.
		onSubmit?.({
			imageGenerationModel: allowImageModelSelection ? selectedImageGenerationModel : 'none',
			selectedTheme: selectedThemeValue ?? 'none',
		});

		// Track that user initiated AI image generation
		trackAIInteractionInit({
			attributes: {
				promptLength: data.promptInput?.length || 0,
				useRawPrompt,
				selectedTheme: selectedThemeValue ?? 'none',
			},
		});

		try {
			// Generate the AI header image with acceptance flow for repositioning UI
			await generateAIHeaderImage!({
				adfContent: data.promptInput,
				useRawPrompt,
				showAcceptanceFlow: true,
				...(allowImageModelSelection && {
					imageGenerationModel: selectedImageGenerationModel,
				}),
				...(selectedThemeValue && { theme: selectedThemeValue }),
			});

			// Track successful result view
			trackAIResultView({
				attributes: {
					promptLength: data.promptInput?.length || 0,
					useRawPrompt,
					selectedTheme: selectedThemeValue ?? 'none',
				},
			});
		} catch (error) {
			// Track error if generation fails
			trackAIResultError({
				aiErrorMessage: error instanceof Error ? error.message : 'Unknown error',
				aiErrorCode: (error as any)?.code,
			});
		}
	};

	useEffect(() => {
		// Do not update the form state if the image generation is not enabled
		if (!generateAIHeaderImage) {
			return;
		}

		if (!effectivePrefillText) {
			return;
		}

		if (lastAppliedPrefillTextRef.current === effectivePrefillText) {
			return;
		}

		lastAppliedPrefillTextRef.current = effectivePrefillText;
		setFieldValueRef.current?.('promptInput', effectivePrefillText);
	}, [effectivePrefillText, generateAIHeaderImage]);

	if (!generateAIHeaderImage) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.divider} />
			{
				<Form onSubmit={handleFormSubmit}>
					{({ formProps, setFieldValue }: any) => {
						setFieldValueRef.current = setFieldValue;
						return (
							<form {...formProps}>
								<Box xcss={styles.rovoHeader}>
									<Inline space="space.075" alignBlock="center">
										<Box xcss={styles.rovoIcon}>
											<RovoIcon
												appearance="brand"
												size="xsmall"
												label={formatMessage(i18n.rovoIconAlt)}
											/>
										</Box>
										<label htmlFor="get-creative-text-area">
											<Text size="medium" weight="bold" id="get-creative-label">
												{formatMessage(i18n.createImageLabel)}
											</Text>
										</label>
									</Inline>
								</Box>

								<Stack space="space.100">
									<Box>
										<label htmlFor="theme-picker">
											<Text size="small" weight="semibold">
												{formatMessage(i18n.styleLabel)}
											</Text>
										</label>
										<Box paddingBlockStart="space.050">
											<Select<ThemeOption>
												inputId="theme-picker"
												options={themeOptions}
												value={selectedTheme}
												onChange={handleThemeChange}
												placeholder={formatMessage(i18n.pickStyleLabel)}
												isDisabled={isDisabledState}
											/>
										</Box>
									</Box>
									{imageGenerationModelPicker}
								</Stack>

								<Box xcss={styles.imagePromptContainer}>
									{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
									<Box xcss={styles.stack}>
										<Field
											// Remount when the prefill text changes to ensure it overwrites any existing user input.
											key={effectivePrefillText ?? 'promptInput'}
											isRequired
											name="promptInput"
											id="get-creative-text-area"
											defaultValue={effectivePrefillText ?? ''}
										>
											{({ fieldProps }: any) => (
												<Fragment>
													<TextArea
														placeholder={formatMessage(i18n.getCreativePlaceholder)}
														appearance="none"
														{...fieldProps}
														isDisabled={isDisabledState}
													/>
												</Fragment>
											)}
										</Field>
										<Box xcss={styles.actionsRow}>
											<Box xcss={styles.actionsLeft}>{moreActionsButton}</Box>
											<IconButton
												appearance="primary"
												icon={ArrowUpIcon}
												label={formatMessage(i18n.submitButtonLabel)}
												shape="circle"
												type="submit"
												isLoading={isLoading}
												isDisabled={isDisabledState}
											/>
										</Box>
									</Box>
								</Box>
							</form>
						);
					}}
				</Form>
			}
			<Box xcss={styles.footer}>
				<Inline space="space.050" alignBlock="center">
					<InformationCircleIcon color="currentColor" label="" size="small" />
					<Link
						appearance="subtle"
						href="https://www.atlassian.com/trust/atlassian-intelligence"
						target="_blank"
					>
						<Text size="small" color="color.text.subtlest">
							{formatMessage(i18n.footerMessage)}
						</Text>
					</Link>
				</Inline>
			</Box>
		</>
	);
};
